import { Link, useNavigate } from 'react-router-dom';
import { dailySeries, quotasFrom, weeksFrom } from '../../../shared/dashboardData';
import { ArcGauge, ArrowRow, DashLegend, GlowDonut, HalfRings, ProgressRings, TrendLines } from '../../../shared/dashboardCharts';
import { Badge, Card, EmptyState, Loading, ModuleHeader, useAsync } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatCurrency, formatDateTime, formatDuration, formatPercent, formatRelative, SLA_LABELS, SLA_VARIANTS } from '../lib/format';
import { IconAlert, IconDashboard, IconPlus, IconRoute } from '../components/icons';

/** Serienfarben der Diagramme: Profilfarben vorn, dann feste Statusfarben. */
const SERIES = ['var(--primary)', 'var(--accent)', 'var(--info)', 'var(--warning)', 'var(--success)', 'var(--danger)'];

export function DashboardPage() {
  const { user, can, isParked } = useAuth();
  const navigate = useNavigate();
  // Stillgelegte Konten fragen das Lagebild gar nicht erst ab: Die Abfrage
  // verlangt einen Mandanten und käme abgewiesen zurück. Statt einen Fehler
  // zu zeigen, stehen die Kacheln leer da – das ist der Zustand, nicht eine
  // Störung.
  const { data, loading } = useAsync(
    () => (isParked ? Promise.resolve({ cards: {}, noTenant: true }) : api.get('/api/reports/dashboard')),
    [isParked],
  );

  if (loading) return <Loading />;
  if (!data) return <EmptyState title="Keine Daten verfügbar" />;

  const cards = data.cards ?? {};

  const quotas = quotasFrom(cards, SERIES);
  const { weeks, trend: weekTrend } = weeksFrom(data.completedWeekly, SERIES.slice(0, 5));

  const lage = [
    { label: 'Offene Aufträge', value: cards.open, tone: 'var(--primary)', to: '/tasks?open=true' },
    { label: 'Überfällig (SLA)', value: cards.overdue, tone: 'var(--danger)', to: '/tasks?overdue=true' },
    { label: 'Heute fällig', value: cards.dueToday, tone: 'var(--warning)', to: '/tasks?dueToday=true&open=true' },
    { label: 'Nicht zugewiesen', value: cards.unassigned, tone: 'var(--accent)', to: '/dispatch' },
  ];

  const critSegments = (data.byCriticality ?? []).map((entry: any) => ({ label: entry.name, value: entry.count, color: entry.color ?? 'var(--text-subtle)' }));
  const critTotal = critSegments.reduce((sum: number, s: any) => sum + s.value, 0);
  const statusSegments = (data.byStatus ?? []).map((entry: any, i: number) => ({ label: entry.label, value: entry.count, color: SERIES[i % SERIES.length] }));
  const statusTotal = statusSegments.reduce((sum: number, s: any) => sum + s.value, 0);
  const teamRows = (data.workload ?? []).slice(0, 6);
  const teamMax = Math.max(1, ...teamRows.map((row: any) => row.open_tasks));
  const emergency = cards.emergency ?? 0;

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconDashboard width={22} height={22} />}
        title={`Guten Tag, ${user?.firstName}!`}
        subtitle={
          <>
            Übersicht über den aktuellen Betrieb. Der angezeigte Datenumfang richtet sich nach Ihren
            Berechtigungen und Objektzuständigkeiten.
          </>
        }
        pill="Lagebild"
        tone="primary"
        actions={
          <>
            {can('dispatch.route') && (
              <button className="btn" onClick={() => navigate('/routes')}>
                <IconRoute width={15} height={15} /> Tour planen
              </button>
            )}
            {can('dispatch.board') && (
              <button className="btn" onClick={() => navigate('/dispatch')}>
                Zur Disposition
              </button>
            )}
            {can('tasks.create') && (
              <button className="btn primary" onClick={() => navigate('/tasks/new')}>
                <IconPlus width={15} height={15} /> Neue Meldung
              </button>
            )}
          </>
        }
      />

      {data?.noTenant && (
        <div className="alert warning">
          <IconAlert width={16} height={16} />
          <span>
            Diesem Konto ist derzeit kein Mandant zugeordnet. Die Fachbereiche sind deshalb nicht verfügbar und die
            Kacheln bleiben leer. Ihr Profil können Sie weiterhin pflegen – für alles Übrige wenden Sie sich an die
            Administration.
          </span>
        </div>
      )}

      {/* Notfallband: Notfälle gehen sonst in der Zahl der offenen Vorgänge unter. */}
      {!data.noTenant && (
        <div className={`dash-alert ${emergency > 0 ? '' : 'calm'}`} role={emergency > 0 ? 'alert' : undefined}>
          <div className="n">{emergency}</div>
          <div className="txt">
            <strong>{emergency > 0 ? 'Akute Notfälle offen' : 'Kein Notfall offen'}</strong>
            <span>{emergency > 0 ? 'Höchste Kritikalitätsstufe – sofort handeln' : 'Alle Vorgänge der höchsten Kritikalitätsstufe sind abgeschlossen'}</span>
          </div>
          <span className="pill">{cards.overdue ?? 0} SLA verletzt</span>
          <span className="pill">{cards.dueToday ?? 0} heute fällig</span>
          <button className="go" onClick={() => navigate(emergency > 0 ? '/tasks?emergency=true&open=true' : '/tasks?open=true')}>
            {emergency > 0 ? 'Sofort handeln →' : 'Alle offenen Aufträge →'}
          </button>
        </div>
      )}

      <div className="dash-row r1">
        <Card title="Quoten im Überblick" subtitle="Je Ring: Erfüllungsgrad in Prozent">
          {quotas.length ? (
            <>
              <HalfRings items={quotas} />
              <div className="dash-legend-inline">
                {quotas.map((q) => (
                  <span key={q.label}>
                    <i style={{ background: q.color }} />
                    {q.label}
                  </span>
                ))}
              </div>
            </>
          ) : (
            <EmptyState title="Noch keine Quoten" hint="Es gibt keine offenen Vorgänge und keine abgeschlossenen mit SLA." />
          )}
        </Card>

        <Card title="Erledigte Aufträge je Kalenderwoche" subtitle={weekTrend}>
          {weeks.some((w: any) => w.value > 0) ? (
            <ArrowRow items={weeks} />
          ) : (
            <EmptyState title="Noch nichts erledigt" hint="In den letzten sechs Wochen wurde kein Vorgang abgeschlossen." />
          )}
        </Card>

        <Card title="Offene Lage" subtitle="Direkt in die Liste springen">
          <div className="dash-lage">
            {lage.map((row) => (
              <button key={row.label} style={{ ['--tone' as string]: row.tone }} onClick={() => navigate(row.to)}>
                <span>{row.label}</span>
                <b>{row.value ?? 0}</b>
              </button>
            ))}
          </div>
        </Card>
      </div>

      <div className="dash-row auto">
        <Card title="Aufträge je Kritikalität" subtitle="Verteilung inkl. offener Vorgänge">
          {critSegments.length ? (
            <div className="dash-pair">
              <GlowDonut segments={critSegments} centerValue={String(critTotal)} centerLabel="Vorgänge" explodeFirst />
              <DashLegend
                items={critSegments.map((s: any) => ({
                  label: s.label,
                  value: critTotal ? formatPercent((s.value / critTotal) * 100) : '–',
                  color: s.color,
                }))}
              />
            </div>
          ) : (
            <EmptyState title="Noch keine Aufträge erfasst" />
          )}
        </Card>

        <Card title="Aufträge nach Status" subtitle="Aktuelle Verteilung im Lebenszyklus">
          {statusSegments.length ? (
            <div className="dash-pair">
              <GlowDonut segments={statusSegments} centerValue={String(statusTotal)} centerLabel="Vorgänge" />
              <DashLegend items={statusSegments.map((s: any) => ({ label: s.label, value: String(s.value), color: s.color }))} />
            </div>
          ) : (
            <EmptyState title="Keine Daten" />
          )}
        </Card>

        <Card title="Auftragsaufkommen (30 Tage)" subtitle={`${cards.createdLast30 ?? 0} neue · ${cards.completedLast30 ?? 0} erledigte Vorgänge`}>
          <TrendLines
            ariaLabel={`Neue und erledigte Vorgänge der letzten 30 Tage: ${cards.createdLast30 ?? 0} neu, ${cards.completedLast30 ?? 0} erledigt`}
            series={[
              { values: dailySeries(data.trend, 'created'), color: 'var(--primary)' },
              { values: dailySeries(data.trendCompleted, 'completed'), color: 'var(--accent)' },
            ]}
          />
          <div className="dash-legend-inline">
            <span>
              <i style={{ background: 'var(--primary)' }} />
              Neu eingegangen
            </span>
            <span>
              <i style={{ background: 'var(--accent)' }} />
              Erledigt
            </span>
          </div>
        </Card>
      </div>

      {(data.workload || cards.slaQuota != null || data.costs) && (
        <div className="dash-row auto">
          {data.workload && (
            <Card title="Auslastung Team" subtitle="Offene Vorgänge je Mitarbeiter">
              {teamRows.length ? (
                <div className="dash-pair">
                  <ProgressRings
                    items={teamRows.map((row: any, i: number) => ({
                      percent: (row.open_tasks / teamMax) * 100,
                      color: row.overdue_tasks > 0 ? 'var(--danger)' : SERIES[i % 5],
                    }))}
                    centerValue={String(teamRows.reduce((sum: number, row: any) => sum + row.open_tasks, 0))}
                    ariaLabel={`Auslastung Team: ${teamRows.map((row: any) => `${row.first_name} ${row.last_name} ${row.open_tasks} offen`).join(', ')}`}
                  />
                  <DashLegend
                    items={teamRows.map((row: any, i: number) => ({
                      label: `${row.first_name} ${row.last_name}`,
                      value: `${row.open_tasks}${row.overdue_tasks > 0 ? ' ⚠' : ''}`,
                      color: row.overdue_tasks > 0 ? 'var(--danger)' : SERIES[i % 5],
                    }))}
                  />
                </div>
              ) : (
                <EmptyState title="Keine offenen Zuweisungen" />
              )}
            </Card>
          )}
          {cards.slaQuota != null && (
            <Card title="SLA-Erfüllungsquote" subtitle="letzte 90 Tage · Ziel ≥ 95 %">
              <ArcGauge value={cards.slaQuota} label="SLA-Quote" />
            </Card>
          )}
          {data.costs && (
            <Card title="Kosten (30 Tage)" subtitle="Basis für die Weiterberechnung an Eigentümer/Verwalter">
              <div className="dash-lage">
                <div style={{ ['--tone' as string]: 'var(--warning)' }}>
                  <span>Materialkosten</span>
                  <b style={{ fontSize: 20 }}>{formatCurrency(data.costs.material_cost ?? 0)}</b>
                </div>
                <div style={{ ['--tone' as string]: 'var(--info)' }}>
                  <span>Erfasste Arbeitszeit</span>
                  <b style={{ fontSize: 20 }}>{formatDuration(data.costs.labor_minutes)}</b>
                </div>
                <div style={{ ['--tone' as string]: 'var(--accent)' }}>
                  <span>Kalkulierte Personalkosten (55 €/Std.)</span>
                  <b style={{ fontSize: 20 }}>{formatCurrency((data.costs.labor_minutes / 60) * 55)}</b>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      <div className="grid cols-2">
        <Card
          title="Meine offenen Aufträge"
          subtitle={`${cards.mine ?? 0} Vorgänge sind Ihnen zugewiesen`}
          actions={<Link to="/tasks?mine=true" className="btn sm">Alle anzeigen</Link>}
          noBody
        >
          {data.myTasks?.length ? (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Nummer</th>
                    <th>Titel</th>
                    <th>Kritikalität</th>
                    <th>Fällig</th>
                  </tr>
                </thead>
                <tbody>
                  {data.myTasks.map((task: any) => (
                    <tr key={task.id} className="clickable" onClick={() => navigate(`/tasks/${task.id}`)}>
                      <td className="mono">{task.task_no}</td>
                      <td className="truncate" style={{ maxWidth: 260 }}>{task.title}</td>
                      <td>
                        <Badge color={task.color}>{task.criticality_name ?? '–'}</Badge>
                      </td>
                      <td className="small">{task.due_at ? formatRelative(task.due_at) : '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="Keine offenen Aufträge" hint="Ihnen ist aktuell nichts zugewiesen." />
          )}
        </Card>

          {data.escalations && (
            <Card title="Letzte Eskalationen" subtitle="SLA-Warnungen und -Verletzungen der letzten 7 Tage" noBody>
              {data.escalations.length ? (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Auftrag</th>
                        <th>Grund</th>
                        <th>Stufe</th>
                        <th>Zeitpunkt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.escalations.map((event: any) => (
                        <tr key={event.id} className="clickable" onClick={() => navigate(`/tasks/${event.task_id}`)}>
                          <td className="mono">{event.task_no}</td>
                          <td>
                            <Badge variant={event.reason === 'warning' ? 'warning' : 'danger'}>
                              {reasonLabel(event.reason)}
                            </Badge>
                          </td>
                          <td>{event.stage}</td>
                          <td className="small subtle">{formatDateTime(event.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState title="Keine Eskalationen" hint="Alle Fristen wurden eingehalten." />
              )}
            </Card>
          )}
      </div>
    </div>
  );
}

function reasonLabel(reason: string): string {
  return (
    {
      warning: 'Frist läuft ab',
      response_overdue: 'Reaktionszeit überschritten',
      resolution_overdue: 'Erledigungsfrist überschritten',
    }[reason] ?? reason
  );
}

export { SLA_LABELS, SLA_VARIANTS };
